import React, { ChangeEvent, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Select } from '@backstage/core-components';
import { FormControl, FormHelperText, Box, TextField } from '@material-ui/core';
import { ACCOUNT_USAGE_OPTIONS, DEVOPS_EMAIL_OPTIONS, APPLICATION_CATEGORIES, BRAND_OPTIONS, APPLICATION_TYPE_ACCOUNT_USAGES } from './const';
import { useAccountInformationForm } from './useAccountInformationForm';
import { FormData, AccountUsageType, ApplicationCategory } from './types';
import { AccountUsageField } from './components/AccountUsageField';
import { TeamMembersField } from './components/TeamMembersField';
import { EnvironmentField } from './components/EnvironmentField';

export const AccountTypeField = ({
  onChange,
  errorSchema,
  formData
}: FieldExtensionComponentProps<FormData>) => {
  const {
    localFormData,
    teamMemberError,
    handleAccountUsageChange,
    handleFieldChange,
    handleEnvironmentToggle,
    handleTeamMemberDelete,
    handleTeamMemberAdd,
    handleTeamMemberInputChange,
  } = useAccountInformationForm({onChange, formData});

  const selectedAccountUsage = useMemo(() => ACCOUNT_USAGE_OPTIONS.find(item => item.value === localFormData.account_usage), [localFormData.account_usage]);

  return (
    <Box>
      <AccountUsageField
        selectedValue={selectedAccountUsage?.value || ''}
        onChange={handleAccountUsageChange}
        error={!!errorSchema?.account_usage}
        errorMessage={errorSchema?.account_usage?.__errors?.[0]}
      />

      {APPLICATION_TYPE_ACCOUNT_USAGES.includes(localFormData.account_usage) && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <FormControl margin="normal" fullWidth required error={!!errorSchema?.application_name}>
            <TextField
              label="Application Name"
              required
              value={localFormData.application_name || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChange('application_name', e.target.value)}
            />
            <FormHelperText>
              Name of the application this account will be used for.
            </FormHelperText>
            {errorSchema?.application_name?.__errors && (
              <FormHelperText error>{errorSchema.application_name.__errors}</FormHelperText>
            )}
          </FormControl>

          <FormControl margin="normal" fullWidth required error={!!errorSchema?.application_category}>
            <Select
              label="Application Category"
              selected={localFormData.application_category || ApplicationCategory.Agile}
              items={APPLICATION_CATEGORIES}
              onChange={(e) => handleFieldChange('application_category', e as string)}
            />
            <FormHelperText error={false}>
              Select the category that best describes your application.
            </FormHelperText>
            {errorSchema?.application_category?.__errors && (
              <FormHelperText error>{errorSchema.application_category.__errors}</FormHelperText>
            )}
          </FormControl>
        </Box>
      )}
      {/* Only show team members field for non-developer accounts */}
      {(localFormData.account_usage !== AccountUsageType.Developer) && (
        <TeamMembersField
          value={localFormData.team_members}
          onAdd={handleTeamMemberAdd}
          onDelete={handleTeamMemberDelete}
          onChange={handleTeamMemberInputChange}
          error={!!errorSchema?.team_members || !!teamMemberError}
          errorMessage={teamMemberError || errorSchema?.team_members?.__errors?.[0]}
        />
      )}
      {/* Show environment field if the account usage is chosen
       If the account usage is developer, the only available environment is sandbox.
      */}
      {localFormData.account_usage && (
        <>
          <EnvironmentField
            value={localFormData.environment}
            onChange={handleEnvironmentToggle}
            accountUsage={localFormData.account_usage}
            error={!!errorSchema?.environment || localFormData.environment.length === 0}
            errorMessage={errorSchema?.environment?.__errors?.[0]}
            disabled={localFormData.account_usage === AccountUsageType.Developer}
          />

          {/* Devops and non-fuse app accounts can only choose between two admin emails */}
          <FormControl margin="normal" fullWidth error={!!errorSchema?.admin_email} required>
            {(localFormData.account_usage === AccountUsageType.DevOps || localFormData.account_usage === AccountUsageType.NonFuseApp) ? (
              <>
                <Select
                  label="Admin Email"
                  selected={localFormData.admin_email}
                  items={DEVOPS_EMAIL_OPTIONS}  
                  onChange={(e) => handleFieldChange('admin_email', e as string)}
                />
                <FormHelperText>
                  Select the appropriate team email for this account. This email is used by Infosec to contact the team responsible for resolving security incidents and vulnerabilities.
                </FormHelperText>
              </>
            ) : (
              <>
                <TextField
                  label="Admin Email"
                  error={!!errorSchema?.admin_email}
                  value={localFormData.admin_email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChange('admin_email', e.target.value)}
                  required
                />
                <FormHelperText error={false}>
                  Email address of the team lead or distribution group used by Infosec to contact the team responsible for resolving security incidents and vulnerabilities.
                </FormHelperText>
                {errorSchema?.admin_email?.__errors && (
                  <FormHelperText error>{errorSchema.admin_email.__errors}</FormHelperText>
                )}
              </>
            )}
          </FormControl>
          <FormControl margin="normal" fullWidth error={!!errorSchema?.brand}>
            <Select
              label="Brand"
              selected={localFormData.brand}
              items={BRAND_OPTIONS}
              onChange={(e) => handleFieldChange('brand', e as string)}
            />
            <FormHelperText error={false}>
              The brand of the application this account will be used for.
            </FormHelperText>
            {errorSchema?.brand?.__errors && (
              <FormHelperText error>{errorSchema.brand.__errors}</FormHelperText>
            )}
          </FormControl>
        </>
      )}
    </Box>
  );
};
  